import {
  Alert,
  Button,
  Flex,
  IconAddLine,
  Pill,
  Text,
  View,
} from "@instructure/ui";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { AcademicYearService } from "../../../services/AcademicYearService";
import { TermService } from "../../../services/TermService";
import { TermsContext } from "../../../utils/Context";
import { Card } from "../../Card";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { sortTermsAscending, sortTermsDescending } from "../utils";
import TermCard from "./TermCard";
import TermModal from "./TermModal";

const Terms = () => {
  const { insightsTerms } = useContext(TermsContext);
  const [loadingTerms, setLoadingTerms] = useState(true);
  const [defaultTerm, setDefaultTerm] = useState(null);

  useEffect(() => {
    if (insightsTerms.length > 0) {
      setLoadingTerms(false);
    }
  }, [insightsTerms]);

  useEffect(() => {
    // Update default term when insightsTerms is updated
    setDefaultTerm(insightsTerms.find((term) => term.start_at === "default"));
  }, [insightsTerms]);

  return (
    <View as="div">
      <View as="div" textAlign="end" margin="0 0 small 0">
        <AddTermButton />
      </View>
      {loadingTerms ? (
        <Skeleton count={3} height={118} style={{ marginBottom: 15 }} />
      ) : (
        <Flex wrap="wrap" alignItems="start" gap="small">
          {!!defaultTerm && defaultTerm.canvas_terms?.length > 0 && (
            <Flex.Item size="100%">
              <View
                as="div"
                padding="0 0 medium 0"
                margin="0 0 small 0"
                borderWidth="none none small none"
              >
                <Card
                  style={{
                    color: "#0D323F",
                    backgroundColor: "#F2F8FA",
                    border: "1px solid #ccc",
                    borderRadius: 10,
                    padding: "10px 15px",
                  }}
                >
                  <Text weight="bold" as="div">
                    {defaultTerm.name}
                  </Text>
                  <Text lineHeight="double">
                    {t("insightsTerms.associatedTerms")}:{" "}
                    {sortTermsAscending(defaultTerm.canvas_terms).map((t) => (
                      <Pill key={t.id} color="info" margin="0 small 0 0">
                        {t.name}
                      </Pill>
                    ))}
                  </Text>
                </Card>
              </View>
            </Flex.Item>
          )}
          {insightsTerms.length > 0 ? (
            sortTermsDescending(
              insightsTerms.filter((t) => t.start_at !== "default")
            ).map((term) => (
              <Flex.Item size="100%" key={term.id}>
                <TermCard term={term} />
              </Flex.Item>
            ))
          ) : (
            <Flex.Item shouldGrow>
              <Alert>{t("insightsTerms.noTermsAlert")}</Alert>
            </Flex.Item>
          )}
        </Flex>
      )}
    </View>
  );
};

const AddTermButton = () => {
  const {
    canvasTerms,
    setCanvasTerms,
    insightsTerms,
    setInsightsTerms,
    setAcademicYears,
  } = useContext(TermsContext);
  const showNotification = useNotification();
  const [openModal, setOpenModal] = useState(false);

  const handleCreateTerm = async (
    termName,
    startDate,
    endDate,
    includedCanvasTerms,
    academicYear
  ) => {
    try {
      const newTerm = await TermService.createInsightsTerm({
        name: termName,
        start_at: startDate,
        end_at: endDate,
        canvasTerms: includedCanvasTerms,
      });
      if (academicYear) {
        const updatedYear = await AcademicYearService.updateAcademicYear(
          academicYear.id,
          {
            name: academicYear.name,
            start_at: academicYear.start_at,
            end_at: academicYear.end_at,
            insightsTerms: academicYear.insights_terms
              .map((t) => t.id)
              .concat(newTerm.id),
          }
        );
        setAcademicYears((prevYears) =>
          prevYears.map((year) =>
            year.id === updatedYear.id ? updatedYear : year
          )
        );
      }
      let newInsightsTerms = [...insightsTerms];
      if (includedCanvasTerms.length > 0) {
        // Remove included canvas terms from default term
        newInsightsTerms = newInsightsTerms.map((term) => {
          if (term.start_at === "default") {
            return {
              ...term,
              canvas_terms: term.canvas_terms.filter(
                (t) => !includedCanvasTerms.includes(t.id)
              ),
            };
          }
          return term;
        });
        newTerm.canvas_terms = canvasTerms.filter((t) =>
          includedCanvasTerms.includes(t.id)
        );
      }
      newTerm.canvas_terms.forEach((t) => {
        if (!t.insights_term_id) {
          t.insights_term_id = newTerm.id;
          // Add new future canvas term to canvasTerms list
          setCanvasTerms((prevCanvasTerms) => [...prevCanvasTerms, t]);
        } else {
          t.insights_term_id = newTerm.id;
        }
      });
      setInsightsTerms(newInsightsTerms.concat(newTerm));
      setOpenModal(false);
      showNotification(
        t("notifications.general.createSuccess", { obj: "Term" })
      );
    } catch {
      showNotification(
        t("notifications.general.createError", { obj: "Term" }),
        ERROR
      );
    }
  };

  return (
    <>
      <Button
        color="primary"
        renderIcon={IconAddLine}
        onClick={() => setOpenModal(true)}
      >
        {t("insightsTerms.addTerm")}
      </Button>
      <TermModal
        open={openModal}
        setOpen={setOpenModal}
        handleSave={handleCreateTerm}
      />
    </>
  );
};

export { Terms };
